import React from 'react';
import { useBundle } from '@amzn/react-arb-tools';
import { t, resolveContent } from 'src/utils/translation';
import { BUNDLE_NAME, LEARNING_PATHWAY_STRING_ID, CREDENTIAL_STRING_ID } from 'src/constants/localization';
import { PageHeaderV2, PageHeaderV2Props } from '@amzn/seller-university-ui-components';
import { LearningPathDetailData } from 'src/types/state/learningPathway';

interface LearningPathwayHeaderV2Props {
  title: string;
  description: string;
  imageSrc?: string;
  stats: LearningPathDetailData;
  isLocalized?: boolean;
  progress?: PageHeaderV2Props['progress'];
  /**
   * When provided (credential surface), the stats row renders `modules | assessments | duration`
   * — mirroring the homepage credential card — and suppresses the sections stat. When omitted
   * (all regular learning-pathway pages), the row keeps its existing `sections | modules | duration`.
   */
  assessmentCount?: number;
  /** Corner ribbon text overlaid on the top-right of the hero illustration. */
  ribbon?: string;
  eyebrow?: string;
}

const LearningPathwayPageHeaderV2: React.FC<LearningPathwayHeaderV2Props> = ({
  title,
  description,
  imageSrc,
  stats,
  isLocalized = false,
  progress,
  assessmentCount,
  ribbon,
  eyebrow,
}) => {
  const [learningPathwayBundle] = useBundle(BUNDLE_NAME.LEARNING_PATHWAY);
  const [credentialBundle] = useBundle(BUNDLE_NAME.CREDENTIAL);

  // Credential content carries `credential-`-prefixed string IDs that live in the credential
  // bundle and are never pre-localized, so they must always resolve against it. Other pathways
  // keep resolving their title/description against the learning-pathway bundle, honoring
  // isLocalized for content that may already be localized (PolyD).
  const resolveHeaderContent = (content: string) => (
    content.startsWith('credential-')
      ? t(credentialBundle, content)
      : resolveContent(learningPathwayBundle, content, isLocalized)
  );

  const formatLearningPathDetails = (statsData: LearningPathDetailData) => {
    const learningPathType = t(
      learningPathwayBundle,
      LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_PAGE_HEADER_PATH_TYPE,
    );
    const moduleCount = statsData.totalModules
      ?? ((statsData.totalDocuments || 0) + (statsData.totalVideos || 0));
    const durationMinutes = Math.round((statsData.totalDuration || 0) / 60);

    // Credential variant: mirror the homepage credential card's trio (`modules | assessments |
    // duration`), suppressing the sections stat. Triggered when assessmentCount is supplied. Each
    // item is omitted when its count is zero (matching the card), so the shared stats row only
    // carries the parts that apply; regular pathways never reach this branch.
    if (assessmentCount !== undefined) {
      // Credential surfaces render duration in short form ("80 min") from the credential bundle,
      // while regular pathways keep the long "minutes total" string below.
      const shortDuration = t(
        credentialBundle,
        CREDENTIAL_STRING_ID.DURATION_MINUTES_SHORT,
        { minutes: durationMinutes },
      );
      const credentialStats = [
        moduleCount > 0
          ? t(credentialBundle, CREDENTIAL_STRING_ID.MODULES_COUNT, { count: moduleCount })
          : undefined,
        assessmentCount > 0
          ? t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENTS_COUNT, { count: assessmentCount })
          : undefined,
        durationMinutes > 0 ? shortDuration : undefined,
      ].filter((item): item is string => Boolean(item));
      // Pass the pre-filtered trio as a list (mirroring the homepage credential card's `metadata`)
      // so each stat keeps its own label and the shared component joins only the parts that apply —
      // no value can land in a wrongly-named slot and no dangling separator can appear.
      return {
        learningPathType,
        stats: credentialStats,
      };
    }

    // Regular learning-pathway pages keep the long "minutes total" duration string.
    const duration = t(
      learningPathwayBundle,
      LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_DURATION,
      { minutes: durationMinutes },
    );

    return {
      learningPathType,
      totalSections: t(
        credentialBundle,
        CREDENTIAL_STRING_ID.TOTAL_SECTIONS,
        { count: statsData.totalSections || 0 },
      ),
      totalModules: t(
        credentialBundle,
        CREDENTIAL_STRING_ID.TOTAL_MODULES,
        { count: moduleCount },
      ),
      duration,
    };
  };

  return (
    <PageHeaderV2
      title={resolveHeaderContent(title)}
      description={resolveHeaderContent(description)}
      imageSrc={imageSrc}
      learningPathDetail={formatLearningPathDetails(stats)}
      progress={progress}
      ribbon={ribbon}
      eyebrow={eyebrow}
    />
  );
};

export default LearningPathwayPageHeaderV2;
